/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import config from 'cio/config/environment';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { debounce, next } from '@ember/runloop';
import DS from 'ember-data';
import AuthService from 'cio/services/auth';
import {
  ConversionTimeframe,
  ConversionType,
  CONVERSION_TYPES,
  ModelType,
  modelType,
  ModelWithConversion,
} from 'cio/utils/conversions';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';
import GroupCondition from 'cio/app-models/conditions/group';
import serializeFilters from 'cio/utils/conditions/serialize/filter';
import EnvironmentService from 'cio/services/environment';
import { DEFAULT_CONVERSION_WINDOW } from 'cio/controllers/conversions';
import ShortSegmentModel from 'cio/models/short-segment';
import { isEmpty } from '@ember/utils';
import { AndFilter, Filters, OrFilter } from 'cio/utils/filters';
import serializeEventAttribute from 'cio/utils/conditions/serialize/event-attribute';
import { ConditionType } from 'cio/utils/condition-types';
import CampaignModel from 'cio/models/campaign';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import SegmentModel from 'cio/models/segment';

interface ConversionsConfigArgs {
  model: ModelWithConversion;
  campaign?: CampaignModel;
  conversionActions: { label: string; value: string }[];
  allowAnyTimeframe: boolean;
  selectedTimeframeType: ConversionTimeframe;
  maxTimeframe?: number;
  eventConversionCondition: GroupCondition | null;
  onSetEventConversionCondition: (condition: GroupCondition | null) => unknown;
  onSelectTimeframeType: (type: ConversionTimeframe) => unknown;
  onUpdateEventConversionFilters: (filters: Filters | null) => void;
  showEventStorageDataLimitBanner?: () => void;
  hasReadOnlyPermission: boolean;
}

const DEFAULT_MAX_TIMEFRAME = 60 * 60 * 24 * 90; // 90 days

export default class ConversionsConfig extends Component<ConversionsConfigArgs> {
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  readonly ConversionType = ConversionType;
  readonly ConversionTimeframe = ConversionTimeframe;
  readonly conversionTypes = CONVERSION_TYPES;
  readonly conversionTimeframeTypes = [
    { value: ConversionTimeframe.AnyTime, label: 'any time' },
    { value: ConversionTimeframe.Within, label: 'within' },
  ];
  readonly ModelType = ModelType;
  readonly defaultConversionWindow = DEFAULT_CONVERSION_WINDOW;

  @tracked segmentOptions: SegmentModel[] = [];

  constructor(owner: unknown, args: ConversionsConfigArgs) {
    super(owner, args);
    this.fetchSegments.perform();
  }

  fetchSegments = task(async () => {
    let segments = await this.store.findAll('segment');
    this.segmentOptions = segments.toArray();
  });

  get showTimeWindow() {
    return this.args.selectedTimeframeType === ConversionTimeframe.Within;
  }

  get maxTimeframe() {
    return this.args.maxTimeframe ?? DEFAULT_MAX_TIMEFRAME;
  }

  get modelType() {
    return modelType(this.args.model);
  }

  get conversionWindow() {
    return this.args.model.conversionWindow ?? this.defaultConversionWindow;
  }

  @action
  selectedTimeframeTypeChanged(type: ConversionTimeframe) {
    if (type === ConversionTimeframe.Within) {
      this.args.model.set('conversionWindow', 0);
    } else {
      // default to 7 days
      this.args.model.set('conversionWindow', 60 * 60 * 24 * 7);
    }
    this.onValidateConversion(true);
  }

  @action
  onValidateConversion(validConversionTimeframe: unknown) {
    this.args.model.set('validConversionTimeframe', validConversionTimeframe);
  }

  @action
  addEventFilter() {
    if (this.args.model.conversionEventName == null) {
      return;
    }

    let newCondition = EventAttributeCondition.create({
      type: ConditionType.ForeignEventAttribute,
      event: this.args.model.conversionEventName,
      name: '',
      operator: 'eq',
      value: '',
      isConversionEventAttribute: true,
      isForeignEventFilterAttribute: true,
    });

    if (this.args.eventConversionCondition == null) {
      this.args.onSetEventConversionCondition(GroupCondition.create({ operator: 'and', children: [newCondition] }));

      return;
    }

    this.args.eventConversionCondition.set('children', [...this.args.eventConversionCondition.children, newCondition]);
    this.updateEventConversionFilters();
  }

  @action
  updateConditionEventName() {
    debounce(this, this.updateChildrenEventName, config.APP.debounceDuration);
  }

  private updateChildrenEventName() {
    if (isEmpty(this.args.model.conversionEventName)) {
      return;
    }
    this.args.eventConversionCondition?.children.forEach((condition: EventAttributeCondition) =>
      condition.set('event', this.args.model.conversionEventName),
    );
  }

  @action
  removeEventFilter(filterToRemove: EventAttributeCondition) {
    if (this.args.eventConversionCondition == null) {
      return;
    }

    if (this.args.eventConversionCondition.children.length === 1) {
      this.args.onSetEventConversionCondition(null);
      this.args.onUpdateEventConversionFilters(null);
      this.args.model.set('conversionEventFilters', null);
    } else {
      this.args.eventConversionCondition.set(
        'children',
        this.args.eventConversionCondition.children.filter((filter) => filterToRemove !== filter),
      );
      this.updateEventConversionFilters();
    }
  }

  @action
  updateEventConversionFilters() {
    next(() => {
      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      if (this.args.eventConversionCondition == null || this.args.eventConversionCondition.children == null) {
        return;
      }

      // TODO: We need serializers to go straight from AbstractConditions to Filters (v2)
      // This is a circuitous route to go to legacy Conditions and then to Filters
      let serializedForeignEventAttributes = this.args.eventConversionCondition.children.map((filter) =>
        serializeEventAttribute(filter as any, false, true, true),
      );
      let serializedFilters = serializeFilters(
        serializedForeignEventAttributes,
        this.args.eventConversionCondition.operator,
        true,
        false,
      ) as AndFilter | OrFilter;
      this.args.model.set('conversionEventFilters', serializedFilters);
      this.args.showEventStorageDataLimitBanner?.();
    });
  }

  @action
  setConversionType(conversion: { label: string; value: string }) {
    this.args.model.set('conversionType', conversion.value);

    // If switching to event, remove the selected segment
    if (this.args.model.isEventConversion) {
      this.args.model.set('conversionSegmentId', null);
    }

    // If switching to enter/leave segment, remove the set event name
    if (this.args.model.isSegmentConversion) {
      this.args.model.setProperties({
        conversionEventName: null,
        conversionEventFilters: null,
      });
      this.args.onSetEventConversionCondition(null);
    }
  }

  @action
  setConversionEventName(eventName: string) {
    this.args.model.set('conversionEventName', eventName);
  }

  @action
  setConversionSegmentId(segment: ShortSegmentModel) {
    this.args.model.set('conversionSegmentId', segment.id);
  }
}
